/**
 * Blinkit-theme product page (``/product/:id``). Product, shop, reviews, variant selection, price / MRP / stock of the
 * picked variant all come from useProductController — the classic page's own logic. The cart control is the shared
 * InlineCartControl bound to the picked variant and to that exact cart line, so ADD then − / + always reflect and
 * change what is really in the cart.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ChevronRight, Clock, Package, Search, ShoppingBag, Star, Store } from "lucide-react";
import { api } from "../../../lib/api";
import { useShopEta } from "../../../lib/useShopEta";
import { useDefaultAddress } from "../../../lib/storefrontHooks";
import { useCartLineQuantity, useCartQuantities } from "../../../lib/useCartQuantities";
import InlineCartControl from "../../../components/InlineCartControl";
import VariantAxisPicker from "../../../components/VariantAxisPicker";
import ProductVariantPicker from "../../../components/ProductVariantPicker";
import BookingModal from "../../../components/BookingModal";
import BkProductTile, { bkInr, discountPct } from "../../../components/blinkit/BkProductTile";
import { BkCard, BkEmptyState, BkPageHeader } from "../../../components/blinkit/BkUI";
import { ProductAttributes, ReviewBlock } from "../_partials/ProductInfoBlocks";
import { LseBoughtTogether, LseBrandSku } from "../../../components/lse/LseProductExtras";
import { useProductController } from "../controllers/useProductController";

function Gallery({ images, name, imgIdx, setImgIdx }) {
    const ref = useRef(null);
    // Keep the scroller and the dots in step (dots → scroll, swipe → dot).
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const target = imgIdx * el.clientWidth;
        if (Math.abs(el.scrollLeft - target) > 4) el.scrollTo({ left: target, behavior: "smooth" });
    }, [imgIdx]);
    const onScroll = () => {
        const el = ref.current;
        if (!el || !el.clientWidth) return;
        const i = Math.round(el.scrollLeft / el.clientWidth);
        if (i !== imgIdx) setImgIdx(i);
    };
    if (!images.length) {
        return (
            <div className="aspect-square max-h-[420px] w-full rounded-2xl bg-muted/60 flex items-center justify-center" data-testid="pdp-image-carousel">
                <Package size={56} className="text-muted-foreground/50" />
            </div>
        );
    }
    return (
        <div data-testid="pdp-image-carousel">
            <div className="relative rounded-2xl bg-card border border-border/80 overflow-hidden">
                <div ref={ref} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar">
                    {images.map((src, i) => (
                        <div key={src + i} className="w-full shrink-0 snap-center aspect-square max-h-[420px] flex items-center justify-center bg-card">
                            <img src={src} alt={i === 0 ? name : ""} className="w-full h-full object-contain" draggable={false} data-testid={i === 0 ? "pdp-image" : undefined} />
                        </div>
                    ))}
                </div>
                {images.length > 1 && (
                    <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
                        {images.map((_, i) => (
                            <button key={i} type="button" onClick={() => setImgIdx(i)} aria-label={`Show image ${i + 1}`} data-testid={`pdp-image-dot-${i}`}
                                    className={`h-2 rounded-full transition-all ${i === imgIdx ? "w-6 bg-primary" : "w-2 bg-foreground/25"}`} />
                        ))}
                    </div>
                )}
            </div>
            {images.length > 1 && (
                <div className="hidden md:flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                    {images.map((src, i) => (
                        <button key={src + i} type="button" onClick={() => setImgIdx(i)} aria-label={`Show image ${i + 1}`}
                                className={`w-16 h-16 shrink-0 rounded-xl overflow-hidden border-2 bg-card ${i === imgIdx ? "border-primary" : "border-border"}`}>
                            <img src={src} alt="" className="w-full h-full object-contain" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Other products from the same shop — same category first. */
function useMoreFromShop(product) {
    const [list, setList] = useState(null);
    useEffect(() => {
        if (!product?.restaurant_id) return undefined;
        let alive = true;
        api.get(`/restaurants/${product.restaurant_id}/products`)
            .then(({ data }) => {
                if (!alive) return;
                const cats = new Set([product.category_id, ...(product.category_ids || [])].filter(Boolean));
                const others = (data || []).filter((p) => p.id !== product.id && p.status !== "INACTIVE");
                const same = others.filter((p) => cats.has(p.category_id) || (p.category_ids || []).some((c) => cats.has(c)));
                const rest = others.filter((p) => !same.includes(p));
                setList([...same, ...rest].slice(0, 12));
            })
            .catch(() => { if (alive) setList([]); });
        return () => { alive = false; };
    }, [product?.id, product?.restaurant_id]); // eslint-disable-line react-hooks/exhaustive-deps
    return list;
}

export default function BkProductView() {
    const c = useProductController();
    const {
        product, shop, imgIdx, setImgIdx, prodSummary, shopSummary, error, lowStockThreshold, cartCount,
        enabledVariants, axes, hasVariantChoice, selection, pickedVariant, pickedVariantId, matrixMode, setAxis,
        displayPrice, displayMrp, trackStock, isSoldOut, isUnavailable, images, variantPickerOpen, setVariantPickerOpen, onVariantPicked,
    } = c;
    const lineQty = useCartLineQuantity(product?.id, pickedVariantId);
    const cartMap = useCartQuantities();
    const more = useMoreFromShop(product);
    const [booking, setBooking] = useState(false);
    const pickerRef = useRef(null);

    const eta = useShopEta(product?.restaurant_id, useDefaultAddress());

    if (error) {
        return (
            <div className="pb-24" data-testid="pdp-root">
                <BkPageHeader title="Product" />
                <BkEmptyState icon={Package} title={error} testid="pdp-error"
                              body={error === "Product not found" ? "It may have been removed or is no longer sold." : "Check your connection and try again."}
                              action={<Link to="/" className="inline-flex h-10 items-center px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">Back to home</Link>} />
            </div>
        );
    }
    if (!product) {
        return (
            <div className="pb-24" data-testid="pdp-loading">
                <BkPageHeader title=" " />
                <div className="px-4 pt-4 lg:grid lg:grid-cols-2 lg:gap-6" aria-hidden="true">
                    <div className="aspect-square max-h-[420px] rounded-2xl bg-muted animate-pulse" />
                    <div className="mt-4 lg:mt-0 space-y-3">
                        <div className="h-6 w-3/4 rounded bg-muted animate-pulse" />
                        <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
                        <div className="h-8 w-1/2 rounded bg-muted animate-pulse" />
                    </div>
                </div>
            </div>
        );
    }

    const isService = product.kind === "SERVICE";
    const shopStars = shopSummary?.count > 0 ? shopSummary.avg : 0;
    const off = discountPct(displayPrice, displayMrp);
    const unit = pickedVariant?.unit_of_measure || product.unit_of_measure || (pickedVariant ? pickedVariant.label : null);
    const needsChoice = hasVariantChoice && !pickedVariant;
    const lowStock = !isSoldOut && !isUnavailable && trackStock && product.stock != null && product.stock > 0 && product.stock <= lowStockThreshold;

    return (
        <div className="pb-44 lg:pb-16 min-w-0" data-testid="pdp-root" data-variant="blinkit">
            <BkPageHeader
                title={<span className="text-[15px] font-bold">{product.name}</span>}
                right={<>
                    <Link to="/search" aria-label="Search" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted"><Search size={20} /></Link>
                    <Link to="/cart" aria-label="View cart" data-testid="pdp-view-cart" className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted">
                        <ShoppingBag size={20} />
                        {cartCount > 0 && (
                            <span data-testid="pdp-view-cart-count" className="absolute top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold flex items-center justify-center border-2 border-card">
                                {cartCount > 9 ? "9+" : cartCount}
                            </span>
                        )}
                    </Link>
                </>}
            />

            <div className="px-4 pt-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-8 lg:items-start">
                <div className="lg:sticky lg:top-20">
                    <Gallery images={images} name={product.name} imgIdx={imgIdx} setImgIdx={setImgIdx} />
                </div>

                <div className="mt-4 lg:mt-0 space-y-4 min-w-0">
                    {/* Name, pack, price */}
                    <section data-testid="pdp-summary">
                        <div className="flex flex-wrap items-center gap-2">
                            {product.is_bestseller && (
                                <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5">
                                    <Star size={10} className="fill-current" /> Bestseller
                                </span>
                            )}
                            {isUnavailable ? (
                                <span className="rounded-md bg-muted text-muted-foreground text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5" data-testid="pdp-unavailable-badge">Currently unavailable</span>
                            ) : isSoldOut ? (
                                <span className="rounded-md bg-destructive/10 text-destructive text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5">Sold out</span>
                            ) : lowStock && (
                                <span className="rounded-md bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5" data-testid="pdp-low-stock">Only {product.stock} left</span>
                            )}
                        </div>
                        <h1 className="mt-1.5 text-[21px] sm:text-[24px] font-extrabold leading-tight tracking-tight" data-testid="pdp-name">{product.name}</h1>
                        {unit && <p className="mt-1 text-[13px] text-muted-foreground" data-testid="pdp-unit">{unit}</p>}
                        <div className="mt-3 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                            {needsChoice && <span className="text-[13px] text-muted-foreground">from</span>}
                            <span className="text-[24px] font-extrabold tabular-nums" data-testid="pdp-price">{bkInr(displayPrice)}</span>
                            {off > 0 && (
                                <>
                                    <span className="text-[14px] text-muted-foreground line-through tabular-nums">MRP {bkInr(displayMrp)}</span>
                                    <span className="rounded-md bg-primary text-primary-foreground text-[11px] font-extrabold px-1.5 py-0.5" data-testid="pdp-off">{off}% OFF</span>
                                </>
                            )}
                        </div>
                        <p className="mt-1 text-[11.5px] text-muted-foreground">Taxes and charges, if any, are shown in your cart before you pay.</p>
                    </section>

                    {hasVariantChoice && (
                        <section ref={pickerRef} className="bg-card rounded-2xl border border-border/80 p-4" data-testid="pdp-variant-section">
                            <h2 className="text-[14px] font-extrabold mb-3">{matrixMode ? "Choose options" : "Choose a size"}</h2>
                            <VariantAxisPicker axes={axes} variants={enabledVariants} selection={selection} onChange={setAxis} />
                        </section>
                    )}

                    {/* Bottom action bar — clears the home indicator / gesture bar. iter178: it lives here in the details column so
                        that from lg it is a card under the price / options (a desktop product page); below lg it is ``fixed``, so
                        its place in the DOM changes nothing on phones and tablets. Never make it the column's first child
                        (``space-y-4`` would then push the summary down). */}
                    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full z-30 bg-card border-t border-border shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.25)] lg:static lg:translate-x-0 lg:z-auto lg:!max-w-none lg:rounded-2xl lg:border lg:shadow-none lg:overflow-hidden"
                         style={{ maxWidth: "var(--shell-width)", paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="pdp-action-bar">
                        {cartCount > 0 && (
                            <Link to="/cart" data-testid="pdp-cart-strip" className="flex items-center justify-between gap-3 px-4 py-2 bg-primary/[0.08] text-primary text-[13px] font-bold">
                                <span className="truncate">{cartCount} item{cartCount === 1 ? "" : "s"} in your cart</span>
                                <span className="inline-flex items-center shrink-0">View cart <ChevronRight size={16} /></span>
                            </Link>
                        )}
                        <div className="px-4 pt-3 flex items-center gap-3">
                            <div className="min-w-0 flex-1 leading-tight">
                                {unit && <p className="text-[11.5px] text-muted-foreground truncate">{unit}</p>}
                                <p className="text-[17px] font-extrabold tabular-nums">
                                    {bkInr(displayPrice)}
                                    {off > 0 && <span className="ml-2 text-[12px] font-semibold text-muted-foreground line-through">{bkInr(displayMrp)}</span>}
                                </p>
                            </div>
                            <div className="w-[152px] shrink-0">
                                {isService ? (
                                    <button type="button" onClick={() => setBooking(true)} data-testid="pdp-book"
                                            className="w-full h-11 rounded-xl bg-primary text-primary-foreground text-[14px] font-bold inline-flex items-center justify-center gap-1.5 active:scale-95 transition">
                                        <CalendarClock size={16} /> Book now
                                    </button>
                                ) : isUnavailable || isSoldOut ? (
                                    <div data-testid={isUnavailable ? "pdp-unavailable" : "pdp-sold-out"}
                                         className="w-full h-11 rounded-xl bg-muted text-muted-foreground text-[12px] font-bold tracking-wider flex items-center justify-center">
                                        {isUnavailable ? "UNAVAILABLE" : "SOLD OUT"}
                                    </div>
                                ) : needsChoice ? (
                                    <button type="button" data-testid="pdp-choose-option"
                                            onClick={() => pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                                            className="w-full h-11 rounded-xl border-2 border-primary text-primary text-[13px] font-bold active:scale-95 transition">
                                        Choose option
                                    </button>
                                ) : (
                                    <div data-testid="pdp-cart-control">
                                        <InlineCartControl product={product} currentQty={lineQty} size="md" fullWidth variantId={pickedVariantId || null} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Delivery + shop */}
                    {shop && (
                        <Link to={`/restaurant/${product.restaurant_id}`} data-testid="pdp-shop-link"
                              className="flex items-center gap-3 bg-card rounded-2xl border border-border/80 p-3.5 active:scale-[0.99] transition">
                            <span className="w-11 h-11 shrink-0 rounded-xl bg-primary/10 text-primary overflow-hidden flex items-center justify-center">
                                {shop.logo_url ? <img src={shop.logo_url} alt="" className="w-full h-full object-cover" /> : <Store size={20} />}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-[14px] font-bold truncate">Sold by {shop.name}</span>
                                <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[12px] text-muted-foreground font-semibold">
                                    {eta && <span className="inline-flex items-center gap-1 text-foreground" data-testid="pdp-eta"><Clock size={12} className="text-primary" /> Delivery in {eta}</span>}
                                    {shopStars > 0 && <span className="inline-flex items-center gap-1"><Star size={12} className="fill-amber-500 text-amber-500" /> {shopStars.toFixed(1)}</span>}
                                </span>
                            </span>
                            <ChevronRight size={18} className="text-muted-foreground shrink-0" />
                        </Link>
                    )}

                    {product.description && (
                        <BkCard title={`About this ${isService ? "service" : "product"}`}>
                            <p className="text-[13.5px] leading-relaxed text-muted-foreground whitespace-pre-line" data-testid="pdp-description">{product.description}</p>
                        </BkCard>
                    )}

                    <div className="[&>div]:mt-0"><ProductAttributes product={product} pickedVariant={pickedVariant} shop={shop} lowStockThreshold={lowStockThreshold} /></div>
                    {/* Location Service Engine tenants only (render nothing otherwise). */}
                    <LseBrandSku product={product} />
                    <LseBoughtTogether productId={product.id} />

                    <BkCard title="Ratings">
                        <ReviewBlock title="This item" summary={prodSummary} emptyText="No ratings yet for this item." />
                        <div className="h-4" />
                        <ReviewBlock title={shop ? `${shop.name} · overall` : "Shop overall"} summary={shopSummary} emptyText="No ratings yet for this shop." />
                    </BkCard>
                </div>
            </div>

            {more && more.length > 0 && (
                <section className="mt-8" data-testid="pdp-more">
                    <div className="px-4 flex items-end justify-between gap-3 mb-3">
                        <h2 className="text-[18px] font-extrabold tracking-tight">More from {shop?.name || "this store"}</h2>
                        <Link to={`/restaurant/${product.restaurant_id}`} className="text-[13px] font-bold text-primary inline-flex items-center shrink-0">See all <ChevronRight size={14} /></Link>
                    </div>
                    <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 pb-1">
                        {more.map((p) => <BkProductTile key={p.id} product={p} qty={cartMap[p.id] || 0} layout="rail" showShop={false} testidPrefix="pdp-more" />)}
                    </div>
                </section>
            )}

            {variantPickerOpen && (
                <ProductVariantPicker product={product} onConfirm={onVariantPicked} onClose={() => setVariantPickerOpen(false)} />
            )}
            {booking && <BookingModal service={product} onClose={() => setBooking(false)} />}
        </div>
    );
}
